import React, { useMemo, useState } from 'react';
import { Trash2Icon } from 'lucide-react';

import i18n from '@/utils/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ItemTypeDisplay } from '@/components/ItemType/ItemTypeDisplay';
import {
  GroupedItemTypeDropdownContent,
  type ItemTypeGroup,
  type ItemTypeGroupKind,
  type MinimalContainerType,
} from '@/views/Order/components/table/ContainerTypeDropdown';
import { useAllItemTypesQuery } from '@/graphql/queries/allItemTypes.generated';
import { ItemTypeCategory } from '@/graphql/generatedTypes';
import type { ContainerType } from '../OrderFulfillment/Components/OrderFulfillmentAddContainerTypes';
import type {
  AllowedEventContainerTypes,
  AllowedEventItemTypes,
} from './types';

// Mirrors colugo's updateEvent gates: containers of these classes, item types
// of these categories.
const EVENT_CONTAINER_CLASSES = new Set([
  'event_portfolio',
  'product_portfolio',
]);
const EVENT_ORDERABLE_ITEM_TYPE_CATEGORIES = new Set<ItemTypeCategory>([
  ItemTypeCategory.ContainerStation,
  ItemTypeCategory.Asset,
]);

type AllowedRowKind = Exclude<ItemTypeGroupKind, 'untracked'>;

type AllowedRow = {
  kind: AllowedRowKind;
  id: number;
  name: string;
  imageUrl: string | null;
  hexCode: string | null;
};

const KIND_ORDER: Record<AllowedRowKind, number> = {
  container: 0,
  container_station: 1,
  asset: 2,
};

function kindLabel(kind: AllowedRowKind): string {
  switch (kind) {
    case 'container':
      return i18n.t('Event:allowedTypesKindContainer');
    case 'container_station':
      return i18n.t('Event:allowedTypesKindContainerStation');
    case 'asset':
      return i18n.t('Event:allowedTypesKindAsset');
  }
}

function kindOfItemType(category: ItemTypeCategory): AllowedRowKind {
  return category === ItemTypeCategory.Asset ? 'asset' : 'container_station';
}

interface EventAllowedTypesTabProps {
  allContainerTypes: ContainerType[];
  allowedContainerTypes: AllowedEventContainerTypes[];
  setAllowedContainerTypes: (
    containerTypes: AllowedEventContainerTypes[],
  ) => void;
  allowedItemTypes: AllowedEventItemTypes[];
  setAllowedItemTypes: (itemTypes: AllowedEventItemTypes[]) => void;
  loading: boolean;
  saveEvent: () => void;
}

/**
 * One list for everything an event's orders may carry — containers and item
 * types (container stations, assets) — stored as the event's two allow lists.
 */
export default function EventAllowedTypesTab({
  allContainerTypes,
  allowedContainerTypes,
  setAllowedContainerTypes,
  allowedItemTypes,
  setAllowedItemTypes,
  loading,
  saveEvent,
}: EventAllowedTypesTabProps) {
  const [addOpen, setAddOpen] = useState(false);

  const { data: itemTypesData, loading: itemTypesLoading } =
    useAllItemTypesQuery({
      variables: { filter: { archived: false }, take: 1000, skip: 0 },
      fetchPolicy: 'cache-first',
    });
  const orderableItemTypes = useMemo(
    () =>
      (itemTypesData?.itemTypes.items ?? []).filter((itemType) =>
        EVENT_ORDERABLE_ITEM_TYPE_CATEGORIES.has(itemType.category),
      ),
    [itemTypesData],
  );

  const allowedContainerTypeIds = new Set(
    allowedContainerTypes.map((ct) => ct.id),
  );
  const allowedItemTypeIds = new Set(allowedItemTypes.map((it) => it.id));

  const rows: AllowedRow[] = useMemo(() => {
    const containerRows = allowedContainerTypes.map((ct): AllowedRow => {
      const full = allContainerTypes.find((c) => c.id === ct.id);
      return {
        kind: 'container',
        id: ct.id,
        name: full?.resolvedName ?? ct.name,
        imageUrl: full?.imageUrl ?? ct.image ?? null,
        hexCode: full?.hexCode ?? ct.color_hex_code ?? null,
      };
    });
    const itemTypeRows = allowedItemTypes.map(
      (it): AllowedRow => ({
        kind: kindOfItemType(it.category),
        id: it.id,
        name: it.name,
        imageUrl: it.image_url ?? null,
        hexCode: it.color_hex_code ?? null,
      }),
    );
    return [...containerRows, ...itemTypeRows].sort(
      (a, b) =>
        KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.name.localeCompare(b.name),
    );
  }, [allowedContainerTypes, allowedItemTypes, allContainerTypes]);

  const dropdownGroups: ItemTypeGroup[] = useMemo(() => {
    const groups: ItemTypeGroup[] = [];
    const containers: MinimalContainerType[] = allContainerTypes
      .filter(
        (ct) =>
          EVENT_CONTAINER_CLASSES.has(ct.class) &&
          !ct.archived &&
          !allowedContainerTypeIds.has(ct.id),
      )
      .map((ct) => ({
        id: ct.id,
        name: ct.resolvedName || ct.name,
        imageUrl: ct.imageUrl,
        hexCode: ct.hexCode,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    if (containers.length > 0) {
      groups.push({
        kind: 'container',
        heading: i18n.t('Order:dropdownContainers'),
        items: containers,
      });
    }
    const toItems = (category: ItemTypeCategory): MinimalContainerType[] =>
      orderableItemTypes
        .filter(
          (it) => it.category === category && !allowedItemTypeIds.has(it.id),
        )
        .map((it) => ({
          id: it.id,
          name: it.name,
          imageUrl: it.image_url,
          hexCode: it.color_hex_code,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    const stations = toItems(ItemTypeCategory.ContainerStation);
    if (stations.length > 0) {
      groups.push({
        kind: 'container_station',
        heading: i18n.t('Order:dropdownContainerStations'),
        items: stations,
      });
    }
    const assets = toItems(ItemTypeCategory.Asset);
    if (assets.length > 0) {
      groups.push({
        kind: 'asset',
        heading: i18n.t('Order:dropdownAssets'),
        items: assets,
      });
    }
    return groups;
    // Sets are derived from the arrays in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allContainerTypes,
    orderableItemTypes,
    allowedContainerTypes,
    allowedItemTypes,
  ]);

  const handleAdd = (picked: MinimalContainerType, group: ItemTypeGroup) => {
    if (group.kind === 'container') {
      const ct = allContainerTypes.find((c) => c.id === picked.id);
      if (!ct) return;
      setAllowedContainerTypes([
        ...allowedContainerTypes,
        {
          id: ct.id,
          name: ct.resolvedName || ct.name,
          image: ct.imageUrl,
          packing_unit: ct.packingUnit,
          segment: null,
          color_hex_code: ct.hexCode,
        },
      ]);
    } else {
      const itemType = orderableItemTypes.find((it) => it.id === picked.id);
      if (!itemType) return;
      setAllowedItemTypes([
        ...allowedItemTypes,
        {
          id: itemType.id,
          name: itemType.name,
          image_url: itemType.image_url,
          color_hex_code: itemType.color_hex_code,
          category: itemType.category,
          archived: itemType.archived,
        },
      ]);
    }
    setAddOpen(false);
  };

  const handleRemove = (row: AllowedRow) => {
    if (row.kind === 'container') {
      setAllowedContainerTypes(
        allowedContainerTypes.filter((ct) => ct.id !== row.id),
      );
    } else {
      setAllowedItemTypes(allowedItemTypes.filter((it) => it.id !== row.id));
    }
  };

  return (
    <div className="tw:space-y-6">
      <div className="tw:flex tw:justify-between tw:items-center tw:gap-4">
        <div>
          <h3 className="tw:text-xl tw:font-semibold">
            {i18n.t('Event:allowedTypes')}
          </h3>
          <p className="tw:text-muted-foreground tw:text-sm">
            {i18n.t('Event:allowedTypesDescription')}
          </p>
        </div>
        <div className="tw:flex tw:items-center tw:gap-2">
          <Popover open={addOpen} onOpenChange={setAddOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="secondary"
                disabled={
                  loading || itemTypesLoading || dropdownGroups.length === 0
                }
              >
                {i18n.t('Event:allowedTypesAdd')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="tw:w-[340px] tw:p-0" align="end">
              <GroupedItemTypeDropdownContent
                groups={dropdownGroups}
                onSelect={handleAdd}
                searchPlaceholder={i18n.t('Order:filterSearchOrderItem')}
                emptyText={i18n.t('Event:allowedTypesNoMore')}
              />
            </PopoverContent>
          </Popover>
          <Button onClick={saveEvent} disabled={loading}>
            {loading ? i18n.t('Common:loading') : i18n.t('Common:save')}
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="tw:text-muted-foreground tw:text-sm tw:py-8 tw:text-center tw:border tw:rounded">
          {i18n.t('Event:allowedTypesEmpty')}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{i18n.t('Order:tableItemType')}</TableHead>
              <TableHead className="tw:w-[200px]">
                {i18n.t('Common:type')}
              </TableHead>
              <TableHead className="tw:w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={`${row.kind}-${row.id}`}>
                <TableCell>
                  <div className="tw:flex tw:items-center tw:gap-2">
                    <ItemTypeDisplay
                      alt={row.name}
                      hexCode={row.hexCode}
                      imageUrl={row.imageUrl}
                      className="tw:size-10"
                      showLargeImageOnHover
                    />
                    <span className="tw:text-sm tw:font-medium">
                      {row.name}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">{kindLabel(row.kind)}</Badge>
                </TableCell>
                <TableCell className="tw:text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={i18n.t('Common:remove')}
                    title={i18n.t('Common:remove')}
                    disabled={loading}
                    onClick={() => handleRemove(row)}
                  >
                    <Trash2Icon className="tw:size-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
